import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, FileDown, AlertTriangle, Loader, Globe, CheckCircle, Clock } from 'lucide-react';
import { ExportModal } from './ExportModal';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { useScanResults } from '../../hooks/useScanResults';
import { ResultsSummary } from './ResultsSummary';
import { ResultsTabs } from './ResultsTabs';
import { formatDateTime } from '../../utils/formatting';
import { GET_PDF_DOWNLOAD_URL, GET_GSC_CONNECTION } from '../../graphql/queries';
import { SET_LINK_REVIEWED } from '../../graphql/mutations';
import type { PageResult } from '../../types';

const client = generateClient();

interface PdfDownloadUrlResponse {
  getPdfDownloadUrl: {
    downloadUrl: string;
    filename: string;
    expiresAt: string;
  } | null;
}

type IssueSeverity = 'critical' | 'serious' | 'moderate' | 'minor';

const IN_PROGRESS_STATUSES = ['PENDING', 'CRAWLING', 'SCANNING', 'AGGREGATING', 'GENERATING_REPORT'];

export function ScanResults() {
  const { siteId, scanId } = useParams<{ siteId: string; scanId: string }>();
  const [activeTab, setActiveTab] = useState<'summary' | 'accessibility' | 'eaa' | 'seo' | 'geo' | 'performance' | 'links' | 'toxic-links' | 'toxic-backlinks' | 'issues' | 'insights'>('summary');
  const [severityFilter, setSeverityFilter] = useState<IssueSeverity | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [gscConnected, setGscConnected] = useState(false);
  const { scan, pageResults, tabData, loadTabData, loadingTabs, isLoading, error } = useScanResults(siteId!, scanId!);

  // Links a person has checked by hand in this scan. Held here because both the
  // header count and the link tabs read it. Server state is the truth; the local
  // copy only exists so the checkbox reacts before the round trip finishes.
  const [reviewedLinks, setReviewedLinks] = useState<string[]>([]);

  useEffect(() => {
    setReviewedLinks(scan?.reviewedLinks ?? []);
  }, [scan?.scanId, scan?.reviewedLinks]);

  // Takes a list so ticking a whole domain is one request and one write.
  const handleToggleLinkReviewed = async (targetUrls: string[], reviewed: boolean) => {
    if (targetUrls.length === 0) return;
    const before = reviewedLinks;

    setReviewedLinks((prev) =>
      reviewed
        ? [...new Set([...prev, ...targetUrls])]
        : prev.filter((url) => !targetUrls.includes(url))
    );

    try {
      const result = (await client.graphql({
        query: SET_LINK_REVIEWED,
        variables: { input: { siteId, scanId, targetUrls, reviewed } },
      })) as GraphQLResult<{ setLinkReviewed: { reviewedLinks?: string[] } }>;

      setReviewedLinks(result.data?.setLinkReviewed?.reviewedLinks ?? []);
    } catch (err) {
      console.error('Failed to save link review:', err);
      setReviewedLinks(before);
    }
  };

  // Merge summary pageResults with loaded tab data (overlays heavy fields by pageUrl).
  // Used by ExportModal so it always has the freshest available data.
  const mergedPageResults = useMemo((): PageResult[] => {
    if (Object.keys(tabData).length === 0) return pageResults;
    const map = new Map<string, PageResult>();
    for (const p of pageResults) map.set(p.pageUrl, { ...p });
    for (const pages of Object.values(tabData)) {
      for (const p of pages) {
        const existing = map.get(p.pageUrl);
        if (existing) {
          if (p.accessibilityIssues !== undefined) existing.accessibilityIssues = p.accessibilityIssues;
          if (p.eaaIssues !== undefined) existing.eaaIssues = p.eaaIssues;
          if (p.seoResult !== undefined) existing.seoResult = p.seoResult;
          if (p.geoResult !== undefined) existing.geoResult = p.geoResult;
          if (p.brokenLinks !== undefined) existing.brokenLinks = p.brokenLinks;
          if (p.toxicLinks !== undefined) existing.toxicLinks = p.toxicLinks;
        }
      }
    }
    return Array.from(map.values());
  }, [pageResults, tabData]);

  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const res = await client.graphql({
          query: GET_GSC_CONNECTION,
          variables: { siteId },
        }) as GraphQLResult<{ getGscConnection: { status: string } | null }>;
        setGscConnected(res.data?.getGscConnection?.status === 'CONNECTED');
      } catch {
        // GSC not connected, default remains false
      }
    })();
  }, [siteId]);

  const handleSeverityClick = (severity: IssueSeverity) => {
    setSeverityFilter(severity);
    setActiveTab('issues');
  };


  const handleDownloadPdf = async () => {
    if (!siteId || !scanId) return;

    setIsDownloading(true);
    setDownloadError(null);

    try {
      // Get the signed URL from the API
      const response = await client.graphql({
        query: GET_PDF_DOWNLOAD_URL,
        variables: { siteId, scanId },
      }) as GraphQLResult<PdfDownloadUrlResponse>;

      const downloadData = response.data?.getPdfDownloadUrl;
      if (!downloadData) {
        throw new Error('PDF is being regenerated. Please wait a moment and try again.');
      }

      // Fetch the PDF using the signed URL
      const pdfResponse = await fetch(downloadData.downloadUrl);
      if (!pdfResponse.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create a blob and trigger download
      const blob = await pdfResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setDownloadError(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !scan) {
    return (
      <div className="py-8">
        <div className="container-content">
          <div className="card text-center py-12">
            <AlertTriangle size={48} className="mx-auto text-status-warning mb-4" />
            <h2 className="text-heading-3 font-semibold mb-2">Results Not Found</h2>
            <p className="text-body text-gray-medium mb-6">
              {error || 'The scan results could not be loaded.'}
            </p>
            <Link to={`/sites/${siteId}`} className="btn-primary">
              Back to Site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isInProgress = IN_PROGRESS_STATUSES.includes(scan.status.toUpperCase());

  // Progress view for in-progress scans
  if (isInProgress) {
    const status = scan.status.toLowerCase();
    const progress = scan.progress;
    const pagesDiscovered = progress?.pagesDiscovered || 0;
    const pagesScanned = progress?.pagesScanned || 0;
    const progressPercent = pagesDiscovered > 0 ? (pagesScanned / pagesDiscovered) * 100 : 0;

    const getStatusText = () => {
      switch (status) {
        case 'pending':
          return 'Initializing scan...';
        case 'crawling':
          return 'Discovering pages...';
        case 'scanning':
          return 'Scanning pages for issues...';
        case 'aggregating':
          return 'Processing results...';
        case 'generating_report':
          return 'Generating PDF report...';
        default:
          return 'Processing...';
      }
    };

    return (
      <div className="py-8">
        <div className="container-content">
          {/* Back Link */}
          <Link
            to={`/sites/${siteId}`}
            className="inline-flex items-center gap-2 text-body-sm text-gray-medium hover:text-coral mb-6"
          >
            <ArrowLeft size={16} />
            Back to Site
          </Link>

          {/* Progress Header */}
          <div className="section-dark rounded-card p-8 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Loader className="text-coral animate-spin" size={32} />
              <div>
                <h1 className="text-heading-1 font-bold text-white">Scan In Progress</h1>
                <p className="text-body text-white/70">{getStatusText()}</p>
              </div>
            </div>

            {/* Progress Info */}
            <div className="grid grid-cols-3 gap-6 mt-6">
              <div>
                <div className="text-display font-bold text-white">{pagesDiscovered}</div>
                <div className="text-body-sm text-white/70">Pages Discovered</div>
              </div>
              <div>
                <div className="text-display font-bold text-coral">{pagesScanned}</div>
                <div className="text-body-sm text-white/70">Pages Scanned</div>
              </div>
              <div>
                <div className="text-display font-bold text-white">{Math.round(progressPercent)}%</div>
                <div className="text-body-sm text-white/70">Complete</div>
              </div>
            </div>

            {/* Progress Bar */}
            {pagesDiscovered > 0 && (
              <div className="mt-6">
                <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-coral transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Current Page */}
            {progress?.currentPage && (
              <div className="mt-4 text-body-sm text-white/60">
                Currently scanning: {progress.currentPage}
              </div>
            )}
          </div>

          {/* Scanned Pages List */}
          <div className="card">
            <div className="p-4 border-b border-gray-light">
              <h2 className="text-heading-3 font-semibold">
                Scanned Pages ({pagesScanned})
              </h2>
            </div>
            {pageResults.length === 0 ? (
              <div className="p-8 text-center text-gray-medium">
                <Globe size={48} className="mx-auto mb-4 opacity-50" />
                <p>Waiting for pages to be scanned...</p>
                <p className="text-body-sm mt-2">Pages will appear here as they are processed</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-light max-h-[500px] overflow-y-auto">
                {pageResults.map((page) => (
                  <div key={page.pageUrl} className="p-4 flex items-center gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-status-success/10 flex items-center justify-center">
                      <CheckCircle size={16} className="text-status-success" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-body font-medium truncate" title={page.pageUrl}>
                        {page.pageUrl}
                      </p>
                      <div className="flex items-center gap-4 text-body-sm text-gray-medium">
                        <span>Score: {page.score}</span>
                        <span>•</span>
                        <span>{page.issueCount} issues</span>
                        {page.scannedAt && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatDateTime(page.scannedAt)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`grade-${page.grade.toLowerCase()} w-10 h-10 text-body`}>
                      {page.grade}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Completed scan view
  return (
    <div className="py-8">
      <div className="container-content">
        {/* Back Link */}
        <Link
          to={`/sites/${siteId}`}
          className="inline-flex items-center gap-2 text-body-sm text-gray-medium hover:text-coral mb-6"
        >
          <ArrowLeft size={16} />
          Back to Site
        </Link>

        {/* Results Summary Header */}
        <ResultsSummary
          scan={scan}
          pageCount={pageResults.length}
          onSeverityClick={handleSeverityClick}
          reviewedLinkCount={reviewedLinks.length}
        />

        {/* Downloads */}
        <div className="mt-4 mb-8 flex flex-wrap items-center gap-3">
          {scan.pdfReportUrl && (
            <button
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Download PDF Report
                </>
              )}
            </button>
          )}
          {pageResults.length > 0 && (
            <button
              onClick={() => {
              setIsExportModalOpen(true);
              // Pre-load all heavy tabs so ExportModal has full data
              ['accessibility', 'eaa', 'seo', 'geo', 'links', 'toxic-links'].forEach(tab => loadTabData(tab));
            }}
            className="btn-secondary inline-flex items-center gap-2"
            >
              <FileDown size={18} />
              Download Issues
            </button>
          )}
          {downloadError && (
            <p className="text-status-error text-body-sm">{downloadError}</p>
          )}
        </div>

        {/* Export Modal */}
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          pageResults={mergedPageResults}
          siteUrl={scan.config.siteUrl}
        />

        {/* Tabs */}
        <ResultsTabs
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            loadTabData(tab);
            if (tab !== 'issues') {
              setSeverityFilter(undefined);
            }
          }}
          scan={scan}
          pageResults={pageResults}
          tabData={tabData}
          loadingTabs={loadingTabs}
          initialSeverityFilter={severityFilter}
          gscConnected={gscConnected}
          reviewedLinks={reviewedLinks}
          onToggleLinkReviewed={handleToggleLinkReviewed}
        />
      </div>
    </div>
  );
}
